/**
 * filterRecords (Unit 5 Phase 2) — the records-grid filter evaluator.
 *
 * PURE, like sortRecords. No React, no Dexie, no storage. Takes the host's
 * records array plus a sanitized condition list and returns a NEW array; the
 * input is never mutated, because the host's `records` is the one-shot snapshot
 * other paths still index into (FieldZonePlaceholder:134-139).
 *
 * VALUES COME FROM record.working[key], NEVER record[key] — the same rule
 * sortRecords.js:10-15 states and for the same reason: the row document carries
 * identity and bookkeeping at the top level and every FIELD VALUE one level
 * down in `working`. Bob reads r[fieldKey] directly (evalFilter:9259) because
 * his prototype records are flat; ours are not, and a direct read here would
 * find `tool_id` and undefined for all 27 others — every condition evaluating
 * against nothing while the UI reports a filter is on.
 *
 * ORDER OF OPERATIONS: the host runs FILTER FIRST, THEN SORT. Sorting the
 * survivors is cheaper than sorting everything and then discarding, and the
 * two are independent — sortRecords is stable, so it cannot reorder rows this
 * module already selected.
 */

import { isEmpty, KIND_BY_KEY, TEXT_COLLATOR } from './fieldValueRules';

/**
 * The four operator sets, keyed on field kind. Ported from Bob's
 * getOperators:9224-9235, MINUS everything we have no kind for or no control
 * for:
 *
 *   his `checkbox` is our `bool`, his `select` is our `enum`
 *   NO date    — there is no kind:'date' in any manifest
 *   NO formula — there is no kind:'formula' in any manifest
 *   NO media   — the MEDIA column carries key:null, belongs to no manifest,
 *                and therefore can never appear as a condition's fieldKey
 *   NO 'is any of' / 'is none of' — the only multi-select operators, and
 *                BottomSheetSelect is single-select (its own docblock, :3-7).
 *                Adding them would mean either a second picker component or
 *                extending a shipped one; both are out of this phase.
 *
 * A kind with no entry falls back to `text`, which is what carries the four
 * no-kind read-only rows (PRELIMINARY TOOL STATUS, TOOL ID and the two
 * server-computed rows) — they hold strings and read as text to a rep.
 * PLATFORM was one of them until it became an editable kind:'text' field; it
 * now reaches this same list through the `text` key rather than the fallback,
 * so its offered operators are unchanged. OEM was one of them until OEM arc
 * Phase 2 made it kind:'enum'; it now takes the `enum` operators.
 */
export const OPERATORS_BY_KIND = {
  text: ['contains', 'does not contain', 'is', 'is not', 'is empty', 'is not empty'],
  number: ['=', '≠', '>', '<', '≥', '≤', 'is empty', 'is not empty'],
  bool: ['is checked', 'is not checked'],
  enum: ['is', 'is not', 'is empty', 'is not empty'],
};

/** Operators that carry their own meaning and take NO value control. */
const VALUELESS_OPERATORS = new Set([
  'is empty', 'is not empty', 'is checked', 'is not checked',
]);

/** The operator list for a field kind. Unknown / absent kind → text. */
export function operatorsForKind(kind) {
  return OPERATORS_BY_KIND[kind] || OPERATORS_BY_KIND.text;
}

/** The operator list for a field KEY, resolved through KIND_BY_KEY. */
export function operatorsForKey(fieldKey) {
  return operatorsForKind(KIND_BY_KEY.get(fieldKey));
}

/**
 * Does this operator need a value control? Bob's
 * operatorNeedsValue:9237-9239, minus his two media operators.
 */
export function operatorNeedsValue(operator) {
  return !VALUELESS_OPERATORS.has(operator);
}

/**
 * Flag truthiness. Bob's is `raw===true||raw==='true'||raw===1`
 * (evalFilter:9276).
 *
 * ⚠ WIDENED, deliberately: the string test is case-INSENSITIVE here. Our own
 * sortRecords boolRank (:79) already folds case, so a row carrying 'TRUE'
 * sorts as checked; without the fold it would simultaneously filter as
 * NOT checked. Two surfaces disagreeing about one flag on one row is a worse
 * outcome than a one-character divergence from the prototype.
 */
function isChecked(raw) {
  return raw === true || raw === 1 || String(raw).toLowerCase() === 'true';
}

/**
 * The distinct non-empty values present in the LOADED records for one field —
 * the option list an enum condition picks from. Bob's
 * getFieldUniqueValues:9219-9222, with two changes: values come from
 * record.working, and the sort is the natural collator above rather than a
 * bare .sort().
 *
 * Deliberately built from what is ON THE DEVICE, not from refdata. A rep
 * filtering a site wants the values that site actually contains; offering the
 * full refdata vocabulary would mostly offer choices that match zero rows.
 */
export function getFieldUniqueValues(records, fieldKey) {
  if (!Array.isArray(records) || !fieldKey) return [];
  const values = new Set();
  for (const record of records) {
    const raw = record.working ? record.working[fieldKey] : undefined;
    if (!isEmpty(raw)) values.add(String(raw));
  }
  return [...values].sort(TEXT_COLLATOR.compare);
}

/**
 * One condition against one record. Ported from Bob's evalFilter:9258-9286 for
 * the operators we keep, with the record.working read and the two divergences
 * marked above.
 *
 * The `default: true` arm is Bob's (:9284) and is the safe direction: an
 * operator this build does not recognise must not silently delete rows. In
 * practice it is unreachable — sanitizeFilter drops any condition whose
 * operator is not in its field kind's set, on read as well as on write.
 */
function evalCondition(record, cond) {
  const raw = record.working ? record.working[cond.fieldKey] : undefined;
  const empty = isEmpty(raw);
  const str = empty ? '' : String(raw).toLowerCase().trim();
  const cval = String(cond.value ?? '').toLowerCase().trim();

  switch (cond.operator) {
    case 'contains': return str.includes(cval);
    case 'does not contain': return !str.includes(cval);
    case 'is': return str === cval;
    case 'is not': return str !== cval;
    case 'is empty': return empty;
    case 'is not empty': return !empty;
    // parseFloat, matching Bob (:9270-9275). NaN on either side makes every
    // comparison false, so a non-numeric value simply never matches a numeric
    // condition rather than throwing or matching everything.
    case '=': return parseFloat(raw) === parseFloat(cval);
    case '≠': return parseFloat(raw) !== parseFloat(cval);
    case '>': return parseFloat(raw) > parseFloat(cval);
    case '<': return parseFloat(raw) < parseFloat(cval);
    case '≥': return parseFloat(raw) >= parseFloat(cval);
    case '≤': return parseFloat(raw) <= parseFloat(cval);
    case 'is checked': return isChecked(raw);
    case 'is not checked': return !isChecked(raw);
    default: return true;
  }
}

/**
 * Apply filter conditions to a records array.
 *
 * @param {Array} records  the host's records (never mutated)
 * @param {Array} filter   sanitized conditions, [{ fieldKey, operator, value }]
 * @param {string} logic   'AND' | 'OR' — anything not exactly 'OR' means AND
 * @returns {Array} a new filtered array — or `records` ITSELF, by reference,
 *   when there is nothing to do. That identity is load-bearing: the host wraps
 *   this in a useMemo, so an unfiltered grid gets the exact array it already
 *   had and React re-renders nothing. Same contract sortRecords holds for an
 *   empty sort.
 */
export default function filterRecords(records, filter, logic) {
  if (!Array.isArray(records)) return records;
  if (!Array.isArray(filter) || filter.length === 0) return records;

  // Defence in depth, mirroring sortRecords:128-132. Conditions arrive
  // sanitized from useFieldFilterState, but this module is exported and pure —
  // a caller handing it junk should get every row back, not a throw.
  const conditions = filter.filter(
    (c) => c && typeof c.fieldKey === 'string' && typeof c.operator === 'string',
  );
  if (conditions.length === 0) return records;

  // Bob's combine, :9291-9296. `every` / `some` also short-circuit, which is
  // what keeps a multi-condition filter over an 841-row site cheap.
  const useOr = logic === 'OR';
  return records.filter((record) => (useOr
    ? conditions.some((cond) => evalCondition(record, cond))
    : conditions.every((cond) => evalCondition(record, cond))));
}
